import { useContext } from "react";
import { AppContext } from "../state/AppContextProvider";
import { ActionType } from "../state/GlobalState";

export enum LogLevel {
  debug,
  info,
  warn,
  error,
}

export interface LogItem {
  name: string;
  date: Date;
  level: LogLevel;
  message: string;
  detail: any;
}

export const formatLogLevel = (l: LogLevel | string) => {
  return isNaN(+l) ? l : LogLevel[l];
};

export const formatLogMessage = (logItem: LogItem): string => {
  return `${logItem.date.toISOString()} ${logItem.name} ${LogLevel[
    logItem.level
  ].toUpperCase()} ${logItem.message}`;
};

export function useLogger(name: string = "App") {
  const { state, dispatch } = useContext(AppContext);

  const mustLog = (level: LogLevel): boolean => {
    return level >= state.logLevel;
  };

  const _log = (level: LogLevel, msg: string, obj: any = null) => {
    const newLog: LogItem = {
      name: name,
      date: new Date(),
      level: level,
      detail: obj,
      message: msg,
    };
    if (mustLog(level)) {
      const formatted = formatLogMessage(newLog);
      switch (level) {
        case LogLevel.debug:
          console.debug(formatted, obj);
          break;
        case LogLevel.info:
          console.info(formatted, obj);
          break;
        case LogLevel.warn:
          console.warn(formatted, obj);
          break;
        case LogLevel.error:
          console.error(formatted, obj);
          break;
      }
      dispatch({ type: ActionType.AddLog, payload: newLog });
    }
  };

  const debug = (msg: string, obj: any = null) => {
    _log(LogLevel.debug, msg, obj);
  };

  const info = (msg: string, obj: any = null) => {
    _log(LogLevel.info, msg, obj);
  };

  const warn = (msg: string, obj: any = null) => {
    _log(LogLevel.warn, msg, obj);
  };

  const error = (msg: string, obj: any = null) => {
    _log(LogLevel.error, msg, obj);
  };

  return {
    debug: debug,
    info: info,
    log: info,
    warn: warn,
    error: error,
    isLoggerReady: state.isLoggerReady,
  };
}

export function useLogs() {
  const { state } = useContext(AppContext);

  return state.logs;
}
